import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { getAppPayments, getAppPaymentAnalytics, refundAppPayment } from '../services/api';

const statusBadge = s => ({ completed:'badge badge-green', pending:'badge badge-amber', refunded:'badge badge-blue', failed:'badge badge-red' })[s] || 'badge badge-gray';

export default function Payments({ toast }) {
  const { token } = useApp();
  const [payments, setPayments] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useState(1);
  const [total, setTotal]       = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const load = async (p=1) => {
    setLoading(true);
    try {
      const [pr, ar] = await Promise.allSettled([
        getAppPayments(token, { page:p, limit:20 }),
        getAppPaymentAnalytics(token),
      ]);
      if (pr.status==='fulfilled') {
        const d = pr.value.data || pr.value;
        const list = d.payments || d.data || [];
        setPayments(Array.isArray(list) ? list : []);
        setTotal(d.total || pr.value.pagination?.total || 0);
        setTotalPages(d.pages || d.totalPages || pr.value.pagination?.totalPages || 1);
      }
      if (ar.status==='fulfilled') {
        const d = ar.value.data || ar.value;
        if (!d?.error) setAnalytics(d);
      }
    } catch(e) { toast?.(e.message,'error'); }
    finally { setLoading(false); }
  };

  useEffect(()=>{ load(); },[token]);

  const refund = async (id) => {
    try { await refundAppPayment(token, id, 'Refunded by admin'); toast?.('Payment refunded'); load(page); }
    catch(e) { toast?.(e.message,'error'); }
  };

  return (
    <div>
      <div className="stats-grid">
        <div className="stat-card"><div className="stat-label">Gross revenue</div><div className="stat-value">${parseFloat(analytics?.totalRevenue||analytics?.grossRevenue||0).toFixed(2)}</div><div className="stat-sub stat-up">All time</div></div>
        <div className="stat-card"><div className="stat-label">Net revenue</div><div className="stat-value">${parseFloat(analytics?.netRevenue||0).toFixed(2)}</div><div className="stat-sub stat-up">After fees</div></div>
        <div className="stat-card"><div className="stat-label">Total payments</div><div className="stat-value">{total}</div><div className="stat-sub">All transactions</div></div>
        <div className="stat-card"><div className="stat-label">Refunded</div><div className="stat-value">{payments.filter(p=>p.status==='refunded').length}</div><div className="stat-sub">This page</div></div>
      </div>

      <div className="card" style={{ padding:0 }}>
        <div className="card-header" style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)' }}>
          <span className="card-title">Payment transactions</span>
          <span style={{ fontSize:12, color:'var(--text-dim)' }}>{total} total</span>
        </div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Payment ID</th><th>Player</th><th>Amount</th><th>Type</th><th>Status</th><th>Date</th><th></th></tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ textAlign:'center', padding:40, color:'var(--text-dim)' }}>Loading payments…</td></tr>
              ) : payments.length===0 ? (
                <tr><td colSpan={7} style={{ textAlign:'center', padding:60, color:'var(--text-dim)' }}>
                  <div style={{ fontSize:32, marginBottom:8 }}>💳</div>
                  <div style={{ fontWeight:500, marginBottom:4 }}>No payments yet</div>
                  <div style={{ fontSize:12 }}>Payments will appear here when players purchase evaluations</div>
                </td></tr>
              ) : payments.map(p=>(
                <tr key={p.id}>
                  <td style={{ fontSize:11, color:'var(--text-dim)', fontFamily:'monospace' }}>{p.id?.slice(0,8)}…</td>
                  <td style={{ fontSize:12 }}>{p.playerName||p.userId||'—'}</td>
                  <td style={{ fontWeight:600, color:'var(--accent)' }}>${parseFloat(p.amount||0).toFixed(2)}</td>
                  <td style={{ fontSize:12 }}>{p.paymentType||p.type||'—'}</td>
                  <td><span className={statusBadge(p.status)}>{p.status}</span></td>
                  <td style={{ fontSize:12, color:'var(--text-dim)' }}>{p.createdAt?new Date(p.createdAt).toLocaleDateString():'—'}</td>
                  <td>{p.status==='completed'&&<button className="btn btn-ghost" style={{ fontSize:11 }} onClick={()=>refund(p.id)}>Refund</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {totalPages>1 && (
        <div style={{ display:'flex', gap:8, justifyContent:'center', marginTop:16 }}>
          <button className="btn btn-ghost" disabled={page<=1} onClick={()=>{setPage(p=>p-1);load(page-1);}}>← Prev</button>
          <span style={{ padding:'8px 14px', fontSize:13, color:'var(--text-muted)' }}>Page {page} / {totalPages}</span>
          <button className="btn btn-ghost" disabled={page>=totalPages} onClick={()=>{setPage(p=>p+1);load(page+1);}}>Next →</button>
        </div>
      )}
    </div>
  );
}
